/**
 * Tutor documents detail tab.
 * Criminal-record file + decision, 360 certification, and contract.
 */
import * as React from 'react';
import {
  Award,
  CheckCircle2,
  AlertCircle,
  Clock,
  Download,
  Eye,
  FileSignature,
  FileText,
  XCircle,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { LabelBadge } from '@/components/common/LabelBadge';
import { useQuery } from '@tanstack/react-query';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { ConfirmModal } from '@/components/common/modals/ConfirmModal';
import { LEARNING360_PATH_STATUS_LABELS } from '@/components/tutors/tutor-configs';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { usePageActions } from '@/hooks/usePageActions';
import { useGetTutorCriminalRecord, useUpdateCriminalRecordDecision } from '@/hooks/useUsers';
import {
  formatTutorShortDate,
  formatLearning360DateTime,
  formatLearning360TimeSpent,
  getTutorCriminalRecordStatusConfig,
  type TutorContentDisplay,
} from '@/components/tutors/inperson/tutor-table-utils';
import { getLearning360TutorPathStats } from '@/services/users.service';
import type { CriminalRecordDecisionAction } from '@/types/user.types';
import {
  fetchCriminalRecordSignedUrl,
  resolveCriminalRecordDisplay,
  resolveCriminalRecordFileName,
} from '@/utils/criminal-record-utils';

/** Onboarding `my_documents.criminalRecord` fields shown in the documents tab. */
type InPersonSectionCriminalRecord = {
  recordNumber?: string;
  issueDate?: string;
  issuedBy?: string;
  uploadDate?: string;
  fileName?: string;
  documentName?: string;
  documentLink?: string;
  status?: string;
  validationStatus?: string;
};

/** Download a URL via blob (falls back to a new tab if fetch fails). */
async function downloadViaBlob(url: string, filename: string): Promise<void> {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank');
  }
}

/** Open a document URL in a new tab. */
function openDocumentPreview(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Label/value row for a 360Learning stat. */
function Learning360StatLine({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm text-muted-foreground">
      <span>{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

/** Fetched 360Learning path stats for one tutor. */
function Learning360PathStatsBlock({ userId }: Readonly<{ userId: string }>) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['learning360-path-stats', userId],
    queryFn: async () => {
      return getLearning360TutorPathStats(userId);
    },
    enabled: Boolean(userId),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Chargement des statistiques…</p>;
  }
  if (isError || !data || data.state === 'unavailable') {
    return (
      <p className="text-sm text-muted-foreground">Statistiques 360 Learning indisponibles.</p>
    );
  }
  if (data.state === 'not_started') {
    return <p className="text-sm text-muted-foreground">Formation non commencée.</p>;
  }

  return (
    <div className="mt-3 space-y-2 rounded-md bg-muted/40 px-3 py-2">
      <Learning360StatLine
        label="Avancement"
        value={data.progress == null ? 'Indisponible' : `${data.progress} %`}
      />
      <Learning360StatLine
        label="Temps passé"
        value={formatLearning360TimeSpent(data.timeSpentMs)}
      />
      <Learning360StatLine label="Démarrage" value={formatLearning360DateTime(data.startedAt)} />
      <Learning360StatLine label="Fin" value={formatLearning360DateTime(data.completedAt)} />
      <Learning360StatLine
        label="Statut"
        value={
          data.status
            ? (LEARNING360_PATH_STATUS_LABELS[data.status] ?? data.status)
            : 'Indisponible'
        }
      />
      <Learning360StatLine
        label="Score"
        value={data.score == null ? 'Indisponible' : `${data.score} %`}
      />
    </div>
  );
}

/** Confirm-modal title for a criminal-record decision. */
function criminalRecordConfirmTitle(
  action: CriminalRecordDecisionAction | null,
  status: string,
): string {
  if (action === 'validate') return 'Valider le casier judiciaire';
  if (action === 'refuse') return 'Refuser le casier judiciaire';
  if (action === 'cancel' && status === 'validated') return 'Annuler la validation';
  if (action === 'cancel') return 'Annuler le refus';
  return '';
}

/** Confirm-modal description for a criminal-record decision. */
function criminalRecordConfirmDescription(action: CriminalRecordDecisionAction | null): string {
  if (action === 'validate') return 'Confirmer la validation ?';
  if (action === 'refuse') return 'Confirmer le refus ?';
  if (action === 'cancel') return 'Remettre en attente de validation ?';
  return '';
}

/** Name + preview/download actions for a stored file URL. */
function DocumentFileRow({ name, url }: Readonly<{ name?: string; url?: string }>) {
  if (!url && !name) return null;
  return (
    <div className="flex items-center justify-between gap-2 p-2 rounded-lg border border-border bg-card/50 overflow-hidden">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
        <span className="text-sm truncate text-foreground">{name ?? 'Document'}</span>
      </div>
      <div className="flex gap-1 shrink-0">
        {url && (
          <>
            <Button size="sm" variant="ghost" onClick={() => openDocumentPreview(url)}>
              <Eye className="w-3.5 h-3.5 mr-1" />
              Aperçu
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => downloadViaBlob(url, name ?? 'document')}
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Télécharger
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** Criminal-record file row with signed-URL preview/download and validate/refuse. */
function CriminalRecordDocumentRow({
  userId,
  companyId,
  canValidate,
  sectionCriminalRecord,
}: Readonly<{
  userId: string;
  companyId: string;
  canValidate: boolean;
  sectionCriminalRecord?: InPersonSectionCriminalRecord;
}>) {
  const { data: criminalRecordData, isLoading } = useGetTutorCriminalRecord(userId, companyId);
  const updateDecision = useUpdateCriminalRecordDecision();
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [action, setAction] = React.useState<CriminalRecordDecisionAction | null>(null);
  const [dlLoading, setDlLoading] = React.useState(false);
  const display = resolveCriminalRecordDisplay({
    criminalRecord: criminalRecordData,
    sectionCriminalRecord,
  });
  const fileName = resolveCriminalRecordFileName(
    criminalRecordData?.fileName,
    sectionCriminalRecord,
  );
  const depositedAt = criminalRecordData?.depositedAt ?? sectionCriminalRecord?.uploadDate;
  const staticUrl = sectionCriminalRecord?.documentLink;
  const hasDeposit = !!criminalRecordData || !!sectionCriminalRecord;

  /** Fetch a signed URL, then the onboarding document link. */
  const fetchSignedUrl = React.useCallback(async (): Promise<string | null> => {
    try {
      setDlLoading(true);
      return await fetchCriminalRecordSignedUrl(userId, staticUrl);
    } finally {
      setDlLoading(false);
    }
  }, [userId, staticUrl]);

  /** Preview the criminal-record file via a signed URL. */
  async function handlePreview(): Promise<void> {
    const url = await fetchSignedUrl();
    if (url) openDocumentPreview(url);
  }

  /** Download the criminal-record file via a signed URL. */
  async function handleDownload(): Promise<void> {
    const url = await fetchSignedUrl();
    if (!url) return;
    await downloadViaBlob(url, fileName);
  }

  /** Open the confirm modal for validate / refuse / cancel. */
  function handleAction(newAction: CriminalRecordDecisionAction): void {
    setAction(newAction);
    setShowConfirm(true);
  }

  function handleValidate(): void {
    handleAction('validate');
  }

  function handleRefuse(): void {
    handleAction('refuse');
  }

  function handleCancel(): void {
    handleAction('cancel');
  }

  function handleCloseConfirm(): void {
    setShowConfirm(false);
  }

  /** Persist the chosen criminal-record decision. */
  function handleConfirm(): void {
    if (!action) return;
    updateDecision.mutate(
      { userId, companyId, action },
      {
        onSuccess: () => {
          if (action === 'validate') toast.success('Casier judiciaire validé');
          else if (action === 'refuse') toast.success('Casier judiciaire refusé');
          else toast.success('Validation annulée');
          setShowConfirm(false);
        },
        onError: (error) => {
          toast.error(getErrorMessage(error));
        },
      },
    );
  }

  function renderMissingPdfHint(): React.ReactNode {
    if (display.hasPdf) return null;
    return (
      <p className="text-xs text-orange-500 mt-2 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {getErrorMessage('criminalRecordPdfRequiredToDecide')}
      </p>
    );
  }

  function renderPendingBanner(): React.ReactNode {
    return (
      <div className="flex items-start gap-3 rounded-lg bg-orange-50 border border-orange-200 p-4 h-full">
        <Clock className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-orange-700">EN ATTENTE DE VALIDATION</p>
          <p className="text-xs text-orange-600 mt-1">
            Veuillez examiner le casier judiciaire et choisir une action.
          </p>
          {renderMissingPdfHint()}
        </div>
      </div>
    );
  }

  function renderRejectedBanner(): React.ReactNode {
    return (
      <div className="flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 p-4 h-full">
        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-red-700">CASIER REFUSÉ</p>
          <p className="text-xs text-red-600 mt-1">
            Refusé le {display.rejectedAt ? formatTutorShortDate(display.rejectedAt) : '—'} par{' '}
            {display.rejectedBy ?? '—'}
          </p>
        </div>
      </div>
    );
  }

  function renderValidatedBanner(): React.ReactNode {
    return (
      <div className="flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 p-4 h-full">
        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-green-700">CASIER VALIDÉ</p>
          <p className="text-xs text-green-600 mt-1">
            Validé le {display.validatedAt ? formatTutorShortDate(display.validatedAt) : '—'} par{' '}
            {display.validatedBy ?? '—'}
          </p>
        </div>
      </div>
    );
  }

  function renderStatusBanner(): React.ReactNode {
    if (display.status === 'rejected') return renderRejectedBanner();
    if (display.status === 'validated') return renderValidatedBanner();
    return renderPendingBanner();
  }

  function renderPendingButtons(): React.ReactNode {
    return (
      <>
        <Button
          size="sm"
          icon={CheckCircle2}
          onClick={handleValidate}
          disabled={!display.hasPdf || updateDecision.isPending}
        >
          Valider
        </Button>
        <Button
          size="sm"
          variant="outline"
          icon={XCircle}
          className="border-red-400 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600"
          onClick={handleRefuse}
          disabled={!display.hasPdf || updateDecision.isPending}
        >
          Refuser
        </Button>
      </>
    );
  }

  function renderRejectedButtons(): React.ReactNode {
    return (
      <>
        <Button
          size="sm"
          icon={CheckCircle2}
          onClick={handleValidate}
          disabled={!display.hasPdf || updateDecision.isPending}
        >
          Valider
        </Button>
        <Button
          size="sm"
          variant="outline"
          icon={Clock}
          onClick={handleCancel}
          disabled={updateDecision.isPending}
        >
          Remettre en attente
        </Button>
      </>
    );
  }

  function renderValidatedButtons(): React.ReactNode {
    return (
      <Button
        size="sm"
        variant="outline"
        icon={XCircle}
        className="border-red-400 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600"
        onClick={handleCancel}
        disabled={updateDecision.isPending}
      >
        Annuler
      </Button>
    );
  }

  function renderActionButtons(): React.ReactNode {
    if (display.status === 'rejected') return renderRejectedButtons();
    if (display.status === 'validated') return renderValidatedButtons();
    return renderPendingButtons();
  }

  function renderDecisionPanel(): React.ReactNode {
    if (!canValidate) return null;
    return (
      <div className="relative rounded-xl border border-border overflow-hidden">
        <div className="flex gap-4 p-4">
          <div className="flex flex-col gap-2 min-w-[140px]">{renderActionButtons()}</div>
          <div className="flex-1">{renderStatusBanner()}</div>
        </div>
      </div>
    );
  }

  function renderDepositedAt(): React.ReactNode {
    if (!depositedAt) return null;
    return (
      <DetailField icon={Clock} label="Date de dépôt" value={formatTutorShortDate(depositedAt)} />
    );
  }

  if (isLoading) return <Spinner size="sm" />;
  if (!hasDeposit)
    return <p className="text-sm text-muted-foreground">Aucun casier judiciaire déposé</p>;

  const criminalRecordStatus = getTutorCriminalRecordStatusConfig(display.status);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Document</span>
        <LabelBadge
          label={criminalRecordStatus.label}
          icon={criminalRecordStatus.icon}
          variant="outlined"
          color={criminalRecordStatus.color}
          size="sm"
        />
      </div>
      {renderDepositedAt()}
      <div className="flex items-center justify-between gap-2 p-2 rounded-lg border border-border bg-card/50 overflow-hidden">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <span className="text-sm truncate text-foreground">{fileName}</span>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button size="sm" variant="ghost" onClick={handlePreview} disabled={dlLoading}>
            <Eye className="w-3.5 h-3.5 mr-1" />
            Aperçu
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDownload} disabled={dlLoading}>
            <Download className="w-3.5 h-3.5 mr-1" />
            Télécharger
          </Button>
        </div>
      </div>
      {renderDecisionPanel()}
      <ConfirmModal
        open={showConfirm}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirm}
        title={criminalRecordConfirmTitle(action, display.status)}
        description={criminalRecordConfirmDescription(action)}
        variant={action === 'validate' ? 'validation' : 'danger'}
        isLoading={updateDecision.isPending}
      />
    </div>
  );
}

/** Props for {@link TutorDocumentsTab}. */
export const TutorDocumentsTab: React.FC<{
  display: TutorContentDisplay;
}> = ({ display }) => {
  const { user: authUser } = useAuthStore();
  const companyId = authUser?.companyId ?? '';
  const pageActions = usePageActions('tutors');
  /** Whether the actor may validate or refuse the criminal record. */
  const canValidateCJ = pageActions?.canValidateCriminalRecord ?? false;
  const { userId, myDocuments, cert360, contract } = display;
  const sectionCriminalRecord = myDocuments.criminalRecord;

  function renderCriminalRecordNumber(): React.ReactNode {
    if (!sectionCriminalRecord?.recordNumber) return null;
    return <DetailField label="Numéro de casier" value={sectionCriminalRecord.recordNumber} />;
  }

  function renderCriminalRecordIssueDate(): React.ReactNode {
    if (!sectionCriminalRecord?.issueDate) return null;
    return (
      <DetailField
        label="Date d'émission"
        value={formatTutorShortDate(sectionCriminalRecord.issueDate)}
      />
    );
  }

  function renderCriminalRecordIssuer(): React.ReactNode {
    if (!sectionCriminalRecord?.issuedBy) return null;
    return <DetailField label="Délivré par" value={sectionCriminalRecord.issuedBy} />;
  }

  function renderCriminalRecordUploadDate(): React.ReactNode {
    if (!sectionCriminalRecord?.uploadDate) return null;
    return (
      <DetailField
        label="Déposé le"
        value={formatTutorShortDate(sectionCriminalRecord.uploadDate)}
      />
    );
  }

  function renderCriminalRecordMeta(): React.ReactNode {
    if (
      !sectionCriminalRecord?.recordNumber &&
      !sectionCriminalRecord?.issueDate &&
      !sectionCriminalRecord?.issuedBy
    ) {
      return null;
    }
    return (
      <div className="mt-4 pt-4 border-t border-border space-y-2">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
          Détails du casier
        </h4>
        {renderCriminalRecordNumber()}
        {renderCriminalRecordIssueDate()}
        {renderCriminalRecordIssuer()}
        {renderCriminalRecordUploadDate()}
      </div>
    );
  }

  return (
    <>
      <DetailsCard icon={FileSignature} title="Casier judiciaire" colorName="color-2" colSpan={2}>
        <CriminalRecordDocumentRow
          userId={userId}
          companyId={companyId}
          canValidate={canValidateCJ}
          sectionCriminalRecord={sectionCriminalRecord}
        />
        {renderCriminalRecordMeta()}
      </DetailsCard>

      <DetailsCard icon={Award} title="Certification 360 Learning" colorName="color-3" colSpan={1}>
        {!cert360.certDocumentLink && !cert360.attestationNumber ? (
          <p className="text-sm text-muted-foreground">Aucune certification renseignée.</p>
        ) : (
          <div className="space-y-3">
            {cert360.attestationNumber && (
              <DetailField label="Numéro d'attestation" value={cert360.attestationNumber} />
            )}
            {cert360.obtentionDate && (
              <DetailField
                label="Date d'obtention"
                value={formatTutorShortDate(cert360.obtentionDate)}
              />
            )}
            {cert360.certDocumentLink && (
              <DocumentFileRow
                name={cert360.certDocumentName ?? 'certification-360.pdf'}
                url={cert360.certDocumentLink}
              />
            )}
          </div>
        )}
        <Learning360PathStatsBlock userId={userId} />
      </DetailsCard>

      <DetailsCard icon={FileSignature} title="Contrat" colorName="color-2" colSpan={1}>
        {!contract.documentLink ? (
          <p className="text-sm text-muted-foreground">Aucun contrat renseigné.</p>
        ) : (
          <div className="space-y-3">
            {contract.isValid !== undefined && (
              <LabelBadge
                label={contract.isValid ? 'Valide' : 'Non valide'}
                icon={contract.isValid ? CheckCircle2 : XCircle}
                variant="outlined"
                color={contract.isValid ? 'rgb(var(--status-success))' : 'rgb(var(--color-error))'}
                size="sm"
              />
            )}
            {contract.signedDate && (
              <DetailField
                label="Date de signature"
                value={formatTutorShortDate(contract.signedDate)}
              />
            )}
            <DocumentFileRow
              name={contract.documentName ?? 'contrat.pdf'}
              url={contract.documentLink}
            />
          </div>
        )}
      </DetailsCard>
    </>
  );
};
